import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import {
  getNotificationPermission,
  getPushSubscriptionStatus,
  subscribeToAllOrganizations,
  disablePushNotificationsForOrgs,
} from "./pushNotifications";

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";
const INK = "#0F1B2A";
const PRIMARY = "#0B5C8A";

/**
 * Reusable notification enable/disable toggle.
 *
 * Always renders a single Switch (unless the browser doesn't support push
 * at all) representing whether THIS device is currently subscribed:
 *
 * - Toggling ON when permission was never asked ("default") opens the
 *   browser's native allow/block popup. If allowed, this device is
 *   subscribed to every org in `orgIds` at once.
 * - Toggling ON when permission is already "granted" just (re)subscribes
 *   this device to every org in `orgIds` at once.
 * - Toggling ON when permission is "denied" retries the request. Browsers
 *   do not allow a website to reopen that popup once a user has explicitly
 *   blocked it, so if it's still blocked afterwards, a dialog explains how
 *   to re-enable it from the browser's site settings. The switch stays off
 *   until the user fixes it in the browser and retries.
 * - Toggling OFF unsubscribes this device from every org in `orgIds` at
 *   once. This only affects the device/browser the toggle was switched off
 *   on - other devices the user is logged in on keep their own
 *   subscription untouched.
 *
 * variant="menu"   -> compact layout for use inside a MUI <Menu>/<MenuItem>
 * variant="banner" -> full-width layout for use on a page (e.g. Reminders)
 */
const NotificationAccess = ({ orgIds = [], variant = "menu" }) => {
  const [permission, setPermission] = useState(getNotificationPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showBlockedHelp, setShowBlockedHelp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = async () => {
      const enabled = await getPushSubscriptionStatus();
      if (!cancelled) setSubscribed(enabled);
    };
    loadStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshPermission = () => setPermission(getNotificationPermission());

  const handleEnable = async () => {
    if (loading || !orgIds.length) return;
    setLoading(true);
    try {
      // Single browser prompt (or silent re-request if already
      // granted/denied) covers every org - all orgs turn on together.
      const result = await subscribeToAllOrganizations(orgIds);
      refreshPermission();

      if (result.granted && result.orgIds.length > 0) {
        setSubscribed(true);

        window.dispatchEvent(
          new CustomEvent("notification-status-changed", {
            detail: { enabled: true },
          }),
        );
      } else {
        setSubscribed(false);
        if (getNotificationPermission() === "denied") {
          setShowBlockedHelp(true);
        }
      }
    } catch (error) {
      console.error("Failed to enable notifications:", error);
      setSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // Unsubscribes every org at once, for this device/browser only.
      await disablePushNotificationsForOrgs(orgIds);
      setSubscribed(false);
      window.dispatchEvent(
        new CustomEvent("notification-status-changed", {
          detail: { enabled: false },
        }),
      );
    } catch (error) {
      console.error("Failed to disable notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (event) => {
    const checked = event.target.checked;
    if (checked) {
      await handleEnable();
    } else {
      await handleDisable();
    }
  };

  if (permission === "unsupported") {
    return null;
  }

  const containerSx =
    variant === "banner"
      ? {
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          background: subscribed ? "#E9F7EE" : "#EAF2FB",
          border: `1px solid ${subscribed ? "#C4E8D2" : "#C9DEF3"}`,
          borderRadius: "12px",
          px: 1.75,
          py: 1,
          mb: 2,
          fontFamily: FONT,
        }
      : { px: 1, py: 0.25, width: "100%", fontFamily: FONT };

  const label = loading
    ? subscribed
      ? "Disabling..."
      : "Enabling..."
    : subscribed
      ? "Push Notifications"
      : permission === "denied"
        ? "Notifications are blocked. Enable notifications"
        : "Enable Notifications";

  return (
    <Box sx={containerSx}>
      <FormControlLabel
        sx={{ m: 0, gap: 0.5 }}
        control={
          <Switch
            checked={subscribed}
            disabled={loading}
            onChange={handleToggle}
            size="small"
            sx={{
              "& .MuiSwitch-switchBase.Mui-checked": { color: "#FFFFFF" },
              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { background: PRIMARY, opacity: 1 },
              "& .MuiSwitch-track": { background: "#C3CDD9", opacity: 1 },
            }}
          />
        }
        label={
          <Typography
            sx={{ fontFamily: FONT }}
            fontSize={13}
            fontWeight={subscribed ? 500 : 600}
            color={
              subscribed
                ? INK
                : permission === "denied"
                  ? "#A6342A"
                  : "#1B4F82"
            }
          >
            {label}
          </Typography>
        }
      />

      <Dialog
        open={showBlockedHelp}
        onClose={() => setShowBlockedHelp(false)}
        PaperProps={{ sx: { borderRadius: "18px", maxWidth: 420, boxShadow: "0 32px 64px -20px rgba(15,27,42,0.5)" } }}
        slotProps={{ backdrop: { sx: { background: "rgba(15,27,42,0.46)" } } }}
      >
        <DialogTitle sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, letterSpacing: "-0.02em", color: INK, pb: 1 }}>
          Notifications are blocked
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontFamily: FONT, lineHeight: 1.5, textWrap: "pretty" }} fontSize={14} color="#4A5A6E">
            Your browser is blocking notifications for this site, so it can't
            show the allow/block prompt again automatically. To turn them back
            on:
          </Typography>
          <Typography
            component="ol"
            fontSize={14}
            color="#3D4C60"
            sx={{
              pl: 2.5,
              mt: 1.5,
              mb: 0,
              py: 1.25,
              pr: 1.5,
              fontFamily: FONT,
              lineHeight: 1.6,
              background: "#F7F9FC",
              border: "1px solid #E4E9EF",
              borderRadius: "12px",
              "& li": { pl: 0.5 },
              "& li::marker": { fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: PRIMARY },
            }}
          >
            <li>Click the lock/info icon next to the address bar</li>
            <li>Find "Notifications" and set it to "Allow"</li>
            <li>Reload this page and switch notifications on again</li>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setShowBlockedHelp(false)}
            sx={{
              height: 40,
              px: 2.5,
              borderRadius: "10px",
              background: PRIMARY,
              color: "#FFFFFF",
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { background: "#084767" },
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationAccess;
